import { CSSProperties, FC, useCallback } from 'react';
import { Box, ButtonBase, Theme, darken, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Connection } from '../types/connection-types';
import ConnectionEnabled from './ConnectionEnabled';
import { resolveIcon } from '../utils/connection-icon-utils';
import { openConnectionModal } from '../utils/selected-connection-store-utils';

const ConnectionItem: FC<React.PropsWithChildren<ConnectionProps>> = props => {
  const { classes } = useStyles();

  const { connection, isUserAdminRole } = props;

  const handleClick = useCallback(() => {
    openConnectionModal(connection.connectionType);
  }, [connection]);

  const tooltipTitle = isUserAdminRole
    ? 'Click to configure'
    : 'Your user role prevents you from performing this action';

  return (
    <Tooltip arrow title={tooltipTitle}>
      <Box
        display='flex'
        flexDirection='row'
        justifyContent='flex-start'
        mt={1}
        mb={4}
        gap={2.5}
        flexWrap='wrap'>
        <ButtonBase
          focusRipple
          className={classes.connectionCard}
          onClick={handleClick}>
          <div className={classes.header}>
            {connection.enabled && <ConnectionEnabled />}
          </div>
          <div className={classes.main}>
            <div>
              <img
                src={resolveIcon(connection.icons)}
                alt={connection.displayName}
                style={{ height: 30 }}
              />
            </div>
            <p className={classes.name}>{connection.displayName}</p>
          </div>
        </ButtonBase>
      </Box>
    </Tooltip>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  connectionCard: {
    width: 120,
    minHeight: 85,
    border: `1px solid transparent !important`,
    borderRadius: `10px !important`,
    padding: `${theme.spacing(1)} !important`,
    background: `${theme.palette.background.paper} !important`,
    justifyContent: 'start !important',
    alignItems: 'start !important',
    flexDirection: 'column',
    transition: theme.transitions.create('background'),
    '&:hover': {
      background: `${darken(theme.palette.divider, 0.2)} !important`,
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: '1.5rem',
    width: '100%',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    alignItems: ' center',
    gap: theme.spacing(1),
  },
  name: {
    ...(theme.typography.body1 as CSSProperties),
    lineHeight: 1.1,
  },
}));

export interface ConnectionProps {
  connection: Connection;
  isUserAdminRole: boolean;
}

export default ConnectionItem;
